import axios from "axios";

const api = axios.create({
  baseURL: "http://localhost:8000",
});

export interface BayesianPrior {
  type: "Normal" | "Beta";
  mean: number;
  std_dev: number;
  alpha?: number;
  beta?: number;
  expert_certainty: number;
}

export interface PerformanceMetrics {
  delta_auc: number;
  shapley_value: number;
}

export interface FeatureDefinition {
  feature_id: string;
  name: string;
  source_logic: string;
  encoding: string;
  bayesian_prior: BayesianPrior;
  current_weight: number;
  performance: PerformanceMetrics;
}

export interface CoefficientShift {
  feature: string;
  before: number;
  after: number;
  shift: number;
  pct_change: number | null;
}

export interface ShadowModelResult {
  feature_id: string;
  champion_auc: number;
  challenger_auc: number;
  delta_auc: number;
  weight_history: number[];
  shapley_value: number;
  coefficient_shifts: CoefficientShift[];
}

export interface PriorVisualization {
  prior: BayesianPrior;
  pdf: { x: number[]; y: number[] };
  samples: number[];
}

export const extractVariable = (transcript: string) =>
  api.post<FeatureDefinition>("/api/extract", { transcript });

export const confirmExtraction = (featureId: string, confirmed: boolean) =>
  api.post("/api/extract/confirm", null, { params: { feature_id: featureId, confirmed } });

export const getPrior = (featureId: string) =>
  api.get<PriorVisualization>(`/api/prior/${featureId}`);

export const updatePrior = (featureId: string, certainty: number, priorType: string = "Normal") =>
  api.post("/api/prior/update", null, { params: { feature_id: featureId, certainty, prior_type: priorType } });

export const runShadowModel = (featureId: string, expertSignal: number = 0.4) =>
  api.post<ShadowModelResult>("/api/shadow/run", null, { params: { feature_id: featureId, expert_signal: expertSignal } });

export const calculatePayout = (featureId: string, baseRate: number = 100) =>
  api.post("/api/payout", { feature_id: featureId, base_rate_per_point: baseRate });

export interface PosteriorResult {
  posterior: {
    posterior_mean: number;
    posterior_std: number;
    prior_mean: number;
    prior_std: number;
    data_mean: number;
    data_std: number;
    n_observations: number;
    prior_weight: number;
    data_weight: number;
  };
  synthetic_preview: Record<string, number>[];
  synthetic_column_distribution: { low: number; med: number; high: number };
}

export const getPosterior = (featureId: string, expertSignal: number = 0.4) =>
  api.post<PosteriorResult>("/api/shadow/posterior", null, { params: { feature_id: featureId, expert_signal: expertSignal } });

export const getDemoDataset = () =>
  api.get<{ feature_names: string[]; n_rows: number; preview: Record<string, number>[] }>("/api/dataset/demo");

export const listFeatures = () => api.get<FeatureDefinition[]>("/api/features");

export const deleteFeature = (featureId: string) => api.delete(`/api/features/${featureId}`);

// ── Dataset & Scanning ──────────────────────────────────────────

export interface GovernanceMeta {
  pii_detected: boolean;
  pii_type: string;
  sensitivity: "low" | "medium" | "high" | "critical";
  quality_score: number;
  completeness: number;
  freshness: "current" | "stale" | "unknown";
  last_updated: string;
  source_system: string;
  owner: string;
  access_level: "open" | "restricted" | "confidential";
  approval_status: "approved" | "pending" | "denied";
  retention_days: number | null;
  compliance_tags: string[];
}

export interface VariableCard {
  name: string;
  description: string;
  dtype: string;
  n_unique: number;
  missing_pct: number;
  mean: number | null;
  std: number | null;
  classification: "observed" | "latent" | "proxy";
  latent_reason: string;
  correlated_with: string[];
  mutual_info_score: number;
  factor_loading: number;
  governance?: GovernanceMeta;
}

export interface DatasetMeta {
  dataset_id: string;
  name: string;
  source: "upload" | "api" | "demo";
  n_rows: number;
  n_cols: number;
  variables: VariableCard[];
  target_column: string | null;
}

export interface ScanResult {
  dataset_id: string;
  n_observed: number;
  n_latent: number;
  n_proxy: number;
  variables: VariableCard[];
  covariance_clusters: string[][];
  suggested_latent: string[];
}

export const uploadDataset = (file: File, targetColumn?: string) => {
  const formData = new FormData();
  formData.append("file", file);
  if (targetColumn) formData.append("target_column", targetColumn);
  return api.post<DatasetMeta>("/api/dataset/upload", formData);
};

export interface ConnectRequest {
  provider: string;
  account: string;
  warehouse: string;
  database: string;
  schema_name: string;
  table: string;
  auth_method: string;
  target_column: string;
}

export const connectDataset = (req: ConnectRequest) =>
  api.post<DatasetMeta>("/api/dataset/connect", req);

export interface SnowflakeConnectRequest {
  account: string;
  user: string;
  password: string;
  warehouse: string;
  database: string;
  schema_name: string;
  table: string;
  target_column?: string;
  row_limit?: number;
}

export const connectSnowflake = (req: SnowflakeConnectRequest) =>
  api.post<DatasetMeta>("/api/dataset/snowflake", req);

export const scanDataset = (datasetId: string) =>
  api.post<ScanResult>(`/api/dataset/${datasetId}/scan`);

export const listDatasets = () => api.get<DatasetMeta[]>("/api/datasets");

export const getDataset = (datasetId: string) =>
  api.get<{ meta: DatasetMeta; preview: Record<string, unknown>[] }>(`/api/dataset/${datasetId}`);

export const extractFromTranscript = (transcript: string) =>
  api.post<{ n_extracted: number; features: FeatureDefinition[]; summary: string }>("/api/extract/transcript", { transcript });

// ── Claude-Powered Insight Extraction ──────────────────────────

export interface ExtractedInsightAPI {
  variable_name: string;
  description: string;
  type: "variable" | "belief" | "threshold";
  predictive_power: number;
  certainty: number;
  prior_mean: number;
  prior_std: number;
  mechanism: string;
  thresholds: string[];
  boundary_conditions: string[];
  source_quote: string;
}

export interface InsightsExtractionResult {
  variables: ExtractedInsightAPI[];
  summary: string;
  expert_name: string;
}

export const extractInsights = (transcript: string, expertName: string = "") =>
  api.post<InsightsExtractionResult>("/api/extract/insights", { transcript, expert_name: expertName });

// ── Interview Chat Agent ──────────────────────────────────────

export interface ChatResponse {
  reply: string;
  session_id: string;
  turn_count: number;
  transcript_length: number;
  suggested_followups?: string[];
}

export interface InterviewChatOptions {
  mode?: "interviewer" | "expert";
  expert_name?: string;
  expert_title?: string;
  expert_bio?: string;
  variable_topic?: string;
}

export const interviewChat = (sessionId: string, message: string, options?: InterviewChatOptions) =>
  api.post<ChatResponse>("/api/interview/chat", {
    session_id: sessionId,
    message,
    ...options,
  });

export const getInterviewTranscript = (sessionId: string) =>
  api.get<{ session_id: string; transcript: string; turn_count: number }>(`/api/interview/${sessionId}/transcript`);

export const listInterviewSessions = () =>
  api.get<{ session_id: string; turn_count: number; transcript_length: number; message_count: number }[]>("/api/interview/sessions");

// ── Qualitative Analysis ──────────────────────────────────────

export interface QualExcerpt {
  speaker: string;
  time: string;
  text: string;
}

export interface QualCode {
  id: number;
  label: string;
  color: string;
  count: number;
  excerpt_indices: number[];
}

export interface QualTheme {
  name: string;
  description: string;
  codes: string[];
  saturation: number;
  sentiment: string;
}

export interface ThemeRelationship {
  from: string;
  to: string;
  relation: string;
  strength: number;
}

export interface ConnectedAgent {
  name: string;
  status: string;
  icon: string;
  desc: string;
  matches: number | null;
}

export interface QualitativeResult {
  excerpts: QualExcerpt[];
  codes: QualCode[];
  themes: QualTheme[];
  theme_relationships: ThemeRelationship[];
  summary: string;
  connected_agents: ConnectedAgent[];
}

export const analyzeTranscript = (transcript: string, sessionId: string = "") =>
  api.post<QualitativeResult>("/api/qualitative/analyze", { transcript, session_id: sessionId });

export const getQualitativeResult = (sessionId: string) =>
  api.get<QualitativeResult>(`/api/qualitative/${sessionId}`);

// ── Variable Resolution ──────────────────────────────────────

export interface ResolutionPath {
  type: "data_match" | "proxy" | "correlation" | "synthetic";
  score: number;
  reason: string;
  matched_column?: string;
  proxy_columns?: string[];
  top_correlates?: { col: string; r: number; mi: number }[];
  viable?: boolean;
  prior?: { mean: number; std: number } | null;
  sample_equiv?: number;
}

export interface VariableResolution {
  variable_name: string;
  description: string;
  mechanism: string;
  source_quote: string;
  certainty: number;
  prior_mean: number | null;
  prior_std: number | null;
  paths: ResolutionPath[];
  recommended: string;
}

export interface ResolveResult {
  resolutions: VariableResolution[];
  dataset_id: string;
  dataset_columns: string[];
  n_rows: number;
}

export const resolveVariables = (variables: Record<string, unknown>[], datasetId: string = "ds_demo_001") =>
  api.post<ResolveResult>("/api/variables/resolve", { variables, dataset_id: datasetId });

// ── POC: Audit Trail ──────────────────────────────────────────

export interface AuditEvent {
  id: string;
  timestamp: string;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  actor: string;
  details: Record<string, unknown>;
  source_quote: string;
}

export const getAuditTrail = (entityId?: string, entityType?: string, limit: number = 100) =>
  api.get<AuditEvent[]>("/api/audit", { params: { entity_id: entityId || "", entity_type: entityType || "", limit } });

// ── POC: Variable Versioning ──────────────────────────────────

export interface VariableVersion {
  version: number;
  timestamp: string;
  prior_mean: number;
  prior_std: number;
  certainty: number;
  status: string;
  changed_by: string;
  change_reason: string;
}

export const getFeatureVersions = (featureId: string) =>
  api.get<VariableVersion[]>(`/api/features/${featureId}/versions`);

// ── POC: Taxonomy Lifecycle ───────────────────────────────────

export const getFeatureStatus = (featureId: string) =>
  api.get<{ feature_id: string; status: string }>(`/api/features/${featureId}/status`);

export const setFeatureStatus = (featureId: string, status: string, actor: string = "analyst") =>
  api.put(`/api/features/${featureId}/status`, null, { params: { status, actor } });

// ── POC: Entity Resolution ────────────────────────────────────

export interface ResolvedEntity {
  mention: string;
  canonical: string;
  confidence: number;
}

export const resolveEntities = (text: string) =>
  api.post<{ text: string; entities: ResolvedEntity[]; n_resolved: number }>("/api/resolve/entities", null, { params: { text } });

// ── POC: Domain Templates ─────────────────────────────────────

export interface DomainTemplateSummary {
  domain: string;
  name: string;
  description: string;
  n_variables: number;
}

export interface DomainTemplateDetail {
  domain: string;
  name: string;
  description: string;
  variables: {
    variable_name: string;
    description: string;
    type: string;
    prior_mean: number;
    prior_std: number;
    mechanism: string;
  }[];
}

export const listTemplates = () =>
  api.get<DomainTemplateSummary[]>("/api/templates");

export const getTemplate = (domain: string) =>
  api.get<DomainTemplateDetail>(`/api/templates/${domain}`);

export const applyTemplate = (domain: string) =>
  api.post<{ domain: string; n_created: number; features: FeatureDefinition[] }>(`/api/templates/${domain}/apply`);

// ── POC: Semantic Search ──────────────────────────────────────

export interface TranscriptSearchResult {
  session_id: string;
  expert: string;
  similarity: number;
  preview: string;
  timestamp: string;
}

export const searchTranscripts = (query: string, topK: number = 5) =>
  api.post<{ query: string; results: TranscriptSearchResult[] }>("/api/search/transcripts", null, { params: { query, top_k: topK } });

// ── POC: What-If Simulation ───────────────────────────────────

export interface RippleEffect {
  feature: string;
  baseline_coeff: number;
  perturbed_coeff: number;
  delta: number;
  pct_change: number;
}

export interface WhatIfResult {
  feature_id: string;
  feature_name: string;
  original_mean: number;
  perturbed_mean: number;
  perturbation_sigma: number;
  direction: string;
  baseline_auc: number;
  perturbed_auc: number;
  auc_delta: number;
  ripple_effects: RippleEffect[];
  baseline_posterior: Record<string, number>;
  perturbed_posterior: Record<string, number>;
}

export const runWhatIf = (featureId: string, perturbationSigma: number = 2.0, direction: string = "up", nRows: number = 1000) =>
  api.post<WhatIfResult>("/api/whatif", { feature_id: featureId, perturbation_sigma: perturbationSigma, direction, n_rows: nRows });

// ── Model Impact: Before/After with Expert Features ──────────────

export interface ExpertFeature {
  name: string;
  source_column: string;
  transform: "gt" | "lt" | "between" | "raw";
  threshold: number;
  threshold_high?: number;
  expert_variable: string;
}

export interface EngineeredFeature {
  name: string;
  source_column: string;
  transform: string;
  threshold: number;
  description: string;
  expert_variable: string;
  values_1: number | null;
  values_0: number | null;
}

export interface FeatureImpact {
  name: string;
  expert_variable: string;
  importance: number;
  description: string;
}

export interface ModelImpactResult {
  baseline_auc: number;
  enriched_auc: number;
  delta_auc: number;
  pct_improvement: number;
  original_feature_count: number;
  enriched_feature_count: number;
  engineered_features: EngineeredFeature[];
  feature_impacts: FeatureImpact[];
  baseline_top_features: { name: string; importance: number }[];
  preview: Record<string, unknown>[];
}

export const runModelImpact = (datasetId: string, expertFeatures: ExpertFeature[]) =>
  api.post<ModelImpactResult>("/api/model/impact", { dataset_id: datasetId, expert_features: expertFeatures });

export const exportNotebook = (datasetId: string, expertFeatures: ExpertFeature[]) =>
  api.post("/api/export/notebook", { dataset_id: datasetId, expert_features: expertFeatures });
